import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { decryptData } from '../../utils/storageCrypto';
const CreditCoinsModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    consumer_id: '',
    budget_id: '',
    coins_amount: '',
    transaction_category: 'manual_credit',
    transaction_description: '',
    expiry_days: 365
  });
  const [customers, setCustomers] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      fetchBudgets();
    }
  }, [isOpen]);

  const fetchCustomers = async () => {
    try {
      const encryptedToken = localStorage.getItem('token');
      const token = decryptData(encryptedToken);
      const response = await fetch('/api/customers', {
        headers: {
          'x-token': token
        }
      });
      const data = await response.json();
      if (data.success) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchBudgets = async () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    try {
      const response = await fetch('/api/buildcoins/budgets?budget_status=active', {
        headers: {
          'x-token': token
        }
      });
      const data = await response.json();
      if (data.success) {
        setBudgets(data.data.budgets);
      }
    } catch (error) {
      console.error('Error fetching budgets:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.consumer_id) newErrors.consumer_id = 'Customer is required';
    if (!formData.budget_id) newErrors.budget_id = 'Budget is required';
    if (!formData.coins_amount || formData.coins_amount <= 0) {
      newErrors.coins_amount = 'Valid coin amount is required';
    }
    if (!formData.transaction_description) {
      newErrors.transaction_description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        coins_amount: parseInt(formData.coins_amount),
        expiry_days: parseInt(formData.expiry_days)
      });
      
      // Reset form
      setFormData({
        consumer_id: '',
        budget_id: '',
        coins_amount: '',
        transaction_category: 'manual_credit',
        transaction_description: '',
        expiry_days: 365
      });
      setErrors({});
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Credit BuildCoins</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Customer Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer *
            </label>
            <select
              value={formData.consumer_id}
              onChange={(e) => setFormData({ ...formData, consumer_id: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.consumer_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Customer</option>
              {customers.map((customer) => (
                <option key={customer._id} value={customer._id}>
                  {customer.customer_name} - {customer.email}
                </option>
              ))}
            </select>
            {errors.consumer_id && (
              <p className="mt-1 text-sm text-red-600">{errors.consumer_id}</p>
            )}
          </div>

          {/* Budget Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget *
            </label>
            <select
              value={formData.budget_id}
              onChange={(e) => setFormData({ ...formData, budget_id: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.budget_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Budget</option>
              {budgets.map((budget) => (
                <option key={budget._id} value={budget._id}>
                  {budget.budget_name} (Available: {budget.remaining_budget.toLocaleString()})
                </option>
              ))}
            </select>
            {errors.budget_id && (
              <p className="mt-1 text-sm text-red-600">{errors.budget_id}</p>
            )}
          </div>

          {/* Coins Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coins Amount *
            </label>
            <input
              type="number"
              min="1"
              value={formData.coins_amount}
              onChange={(e) => setFormData({ ...formData, coins_amount: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.coins_amount ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter coins amount"
            />
            {errors.coins_amount && (
              <p className="mt-1 text-sm text-red-600">{errors.coins_amount}</p>
            )}
          </div>

          {/* Transaction Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={formData.transaction_category}
              onChange={(e) => setFormData({ ...formData, transaction_category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="manual_credit">Manual Credit</option>
              <option value="purchase_reward">Purchase Reward</option>
              <option value="referral_bonus">Referral Bonus</option>
              <option value="loyalty_bonus">Loyalty Bonus</option>
              <option value="promotional_credit">Promotional Credit</option>
              <option value="compensation">Compensation</option>
            </select>
          </div>

          {/* Expiry Days */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiry Days
            </label>
            <input
              type="number"
              min="1"
              max="3650"
              value={formData.expiry_days}
              onChange={(e) => setFormData({ ...formData, expiry_days: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="365"
            />
            <p className="mt-1 text-xs text-gray-500">
              Coins will expire after this many days (default: 365)
            </p>
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.transaction_description}
              onChange={(e) => setFormData({ ...formData, transaction_description: e.target.value })}
              rows="3"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.transaction_description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter transaction description"
            />
            {errors.transaction_description && (
              <p className="mt-1 text-sm text-red-600">{errors.transaction_description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Credit Coins'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditCoinsModal;
